export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Split a typed or pasted string into candidate email tokens.
 *
 * Comma, semicolon, and whitespace all count as separators so a paste of
 * "a@x.com, b@y.com" or "a@x.com b@y.com" becomes two tokens.
 *
 * @example
 * parseEmailTokens("  jane@acme.com, bob@other.com ");
 * // ["jane@acme.com", "bob@other.com"]
 *
 * @example
 * parseEmailTokens("not-an-email");
 * // ["not-an-email"]
 */
export const parseEmailTokens = (raw) =>
  String(raw || "")
    .split(/[,;\s]+/)
    .map((token) => token.trim())
    .filter(Boolean);

/**
 * Append newly typed/pasted addresses onto an existing chip list.
 *
 * Valid emails are de-duplicated case-insensitively. Invalid tokens are
 * returned separately so the UI can show an error instead of silently dropping
 * them.
 *
 * @example
 * addEmailChips(["jane@acme.com"], "bob@other.com, Jane@acme.com");
 * // { emails: ["jane@acme.com", "bob@other.com"], invalid: [] }
 *
 * @example
 * addEmailChips([], "hello");
 * // { emails: [], invalid: ["hello"] }
 */
export const addEmailChips = (existing, raw) => {
  const emails = Array.isArray(existing) ? [...existing] : [];
  const seen = new Set(emails.map((email) => email.toLowerCase()));
  const invalid = [];
  parseEmailTokens(raw).forEach((token) => {
    if (!EMAIL_RE.test(token)) {
      invalid.push(token);
      return;
    }
    const key = token.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    emails.push(token);
  });
  return { emails, invalid };
};

/**
 * Join chip addresses into the comma-separated To: value the draft API expects.
 *
 * @example
 * joinEmails(["jane@acme.com", "bob@other.com"]);
 * // "jane@acme.com, bob@other.com"
 */
export const joinEmails = (emails) => (emails || []).join(", ");
